/**
 * Mail a sign-in code to an email from outside the login modal, then open the
 * modal at the code step holding that same address.
 *
 * Guest checkout (to view the order just bought) and guest sell (to post the
 * listing just written) both do this. They used to borrow `sendEmailCode`
 * from email-verification, which asks for an `email_capture` code -- the kind
 * a signed-in member uses to add an address. The login step redeems only
 * `login` codes (pages/api/auth/[...nextauth].ts, `redeemByCode(..., 'login')`),
 * so every code those surfaces sent was refused as "nothing on record". It
 * worked until 2026-09-14, when codes stopped being derivable from the email
 * and started belonging to a purpose; nothing joined the two halves in a test.
 */
import { errorText } from './error-message';
import { z } from 'zod';
import { LS_Set } from './local-storage';
import { trackError, ErrorSeverity } from './error-tracking';
import { getRecaptchaToken } from './recaptcha-token';
import { postWithBotChallenge } from './bot-challenge-client';

/**
 * Where this browser last had a login code sent. The code step refuses a code
 * for any other address (useLoginAuth), so a send from here must record it the
 * same way the modal's own send does, or a stale value blocks the right code.
 */
export const LOGIN_CODE_SENT_TO_KEY = 'loginCodeSentTo';

/** Kept to the code-relevant fields; the route says more on some errors. */
const SendCodeReplySchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  errorCode: z.string().optional(),
});

export interface LoginCodeResult {
  success: boolean;
  /** The server's reason, for diagnostics. Never rendered. */
  detail?: string;
}

export async function sendEmailLoginCode(input: {
  email: string;
  funnelSessionId?: string;
}): Promise<LoginCodeResult> {
  try {
    // The route refuses a send with no reCAPTCHA token (it is how a script
    // that skips the page is told apart from a person), so this path needs one too.
    const recaptchaToken = await getRecaptchaToken('login');
    const response = await postWithBotChallenge('/api/auth/send-code', {
      emailOrPhone: input.email,
      recaptchaToken,
      funnelSessionId: input.funnelSessionId,
      purpose: 'login',
    });
    const parsed = SendCodeReplySchema.safeParse(await response.json());
    if (response.ok && parsed.success && parsed.data.success) {
      LS_Set(LOGIN_CODE_SENT_TO_KEY, input.email);
      return { success: true };
    }
    return {
      success: false,
      detail: parsed.success ? (parsed.data.errorCode ?? parsed.data.error ?? `HTTP ${response.status}`) : `HTTP ${response.status}`,
    };
  } catch (e) {
    const detail = errorText(e);
    trackError('Failed to send a login code', ErrorSeverity.WARNING, {
      component: 'login-code',
      action: 'sendEmailLoginCode',
      extra: { error: detail },
    });
    return { success: false, detail };
  }
}
